import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PracticeList } from '@/types/settings-api';

/**
 * Practitioner-scoped practice list — PROD-763 Practices tab (read-only).
 *
 * GET /api/v1/Practice/ForPractitioner/{doctorSetupId}
 *
 * Same `PracticeList[]` envelope as `usePracticesList` (the org-wide
 * list at `/api/v1/Practice/List/{orgId}`), but filtered server-side to
 * the practices this practitioner is assigned to. Flutter source:
 * `settings_data_source_impl.dart:477` (`doctorPracticeList`).
 *
 * The Flutter tab is read-only — there is no add / edit / remove path
 * (line 142 of `doctor_practices_tab.dart` is a `// TODO Implement
 * logic for add and Edit` that was never completed). We mirror the
 * Flutter scope: render the cards, no mutations.
 *
 * URL prefix note: `/api/v1/Practice/...` (NOT `/api/Practice/...`).
 * Same prefix split documented on `use-practices-list.ts` —
 * Practice_v1 vs Practice are different .NET controllers. Don't
 * normalise.
 */
export function usePractitionerPractices(
  doctorSetupId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['settings', 'practitioner-practices', doctorSetupId],
    queryFn: ({ signal }) => {
      if (!doctorSetupId) {
        throw new Error('usePractitionerPractices called without doctorSetupId');
      }
      return get<PracticeList[]>(
        `/api/v1/Practice/ForPractitioner/${doctorSetupId}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId,
  });
}
